import {
    ChatUserInfo,
    WidgetHeaderConfig,
    WidgetHeaderStatusConfig,
    WidgetMenuConfig,
    WidgetMenuItems,
} from "@xapp/stentor-chat-widget";
import React, { FC, useMemo, useState } from "react";

import { Avatar } from "../Avatar";
import { ButtonGroup } from "../ButtonGroup";
import { CancelButton } from "../CancelButton";
import { ChatMenu } from "../ChatMenu";
import { DrawerBars } from "../DrawerBars";
import { MenuButton } from "../MenuButton";
import { MinimizeButton } from "../MinimizeButton";
import { RefreshButton } from "../RefreshButton";

import setVisualStatus from "../../store/actions/setVisualStatus";
import { useChatDispatch } from "../../utils/useChatDispatch";
import { log } from "../../utils";

import "./ChatHeader.scss";

export interface Props {
    accountStatus: string; // TODO: use enum
    readonly canRefresh: boolean;
    readonly canMinimize: boolean;
    readonly canCancel: boolean;
    readonly agent: ChatUserInfo;
    readonly menuConfig?: WidgetMenuConfig;
    readonly config?: WidgetHeaderConfig;
    onSubmit?: (message: string) => void;
    refreshOnClick: () => void;
    minimizeOnClick: () => void;
    cancelOnClick: () => void;
}

const DEFAULT_STATUS_CONFIG: WidgetHeaderStatusConfig = {
    online: "Virtual Assistant",
    offline: "Leave us a message",
    away: "We're away!",
    connecting: "Connecting...",
};

function getStatusText(status: string, config?: WidgetHeaderStatusConfig) {
    log(`getStatusText called with status: "${status}"`);
    switch (status) {
        case "online":
            return config?.online ?? DEFAULT_STATUS_CONFIG.online;
        case "offline":
            return config?.offline ?? DEFAULT_STATUS_CONFIG.offline;
        case "away":
            return config?.away ?? DEFAULT_STATUS_CONFIG.away;
        default:
            return config?.connecting ?? DEFAULT_STATUS_CONFIG.connecting;
    }
}

const refreshButtonAriaLabel =
    "To refresh chat click on clockwise gapped circle arrow icon in top right side of widget. ";
const minimizeButtonAriaLabel =
    "To minimize widget click on minus icon in top right side of widget. ";
const closeButtonAriaLabel = "To close widget click on close icon in top right side of widget.";

const ChatHeader: FC<React.PropsWithChildren<Props>> = (props) => {
    const innerDispatch = useChatDispatch();

    const { menuConfig, onSubmit } = props;

    const [drawerOpen, setDrawerState] = useState<boolean>(false); // false initially

    const menuPosition = menuConfig?.menuButtonLocation || "FOOTER";
    const showMenuLeft = menuPosition === "HEADER_LEFT";
    const showMenuRight = menuPosition === "HEADER_RIGHT";
    const menuItemsRaw = menuConfig?.items;
    const menuItemsTabIndex = menuConfig?.itemsTabIndex;
    const menuButtonTabIndex = menuConfig?.button?.tabIndex;
    const menuItems = useMemo<readonly WidgetMenuItems[]>(
        () => (menuItemsRaw ? menuItemsRaw : []),
        [menuItemsRaw]
    );

    // Check if we have a right-side menu
    const hasRightMenu = showMenuRight && menuItems.length > 0;

    function toggleDrawer() {
        const newDrawer = !drawerOpen;
        setDrawerState(newDrawer);
        innerDispatch(
            setVisualStatus({
                drawer: newDrawer,
            })
        );
    }

    function handleMenuItem(label: string) {
        setDrawerState(false);
        onSubmit(label);
    }

    let ariaLabel = "";

    const { canRefresh, canMinimize, canCancel } = props;

    if (canRefresh) {
        ariaLabel = refreshButtonAriaLabel;
    }

    if (canMinimize) {
        ariaLabel = `${ariaLabel}${minimizeButtonAriaLabel}`;
    }

    if (canCancel) {
        ariaLabel = `${ariaLabel}${closeButtonAriaLabel}`;
    }

    return (
        <>
            <div
                className="status-container background-header"
                aria-label={ariaLabel}
                aria-hidden={false}>
                {showMenuLeft && menuItems.length ? (
                    <>
                        <div className="chat-footer__menu-icon">
                            <DrawerBars
                                bars={3}
                                tabIndex={menuButtonTabIndex}
                                onToggle={toggleDrawer}
                            />
                        </div>
                    </>
                ) : (
                    <></>
                )}

                {props?.agent?.avatarPath === undefined ? (
                    <div></div>
                ) : (
                    <div className="status-container__avatar">
                        <Avatar entity={props.agent} />
                    </div>
                )}

                <div
                    className={`
                        ${"status-text"}
                        ${
                            showMenuLeft && menuItems.length
                                ? "status-text-positionWithMenu"
                                : props?.agent?.avatarPath === undefined
                                ? "status-text-positionLeftNoAvatar"
                                : "status-text-positionLeft"
                        }
                        ${
                            props.config?.alignTextCenter
                                ? "status-text-positionCenter"
                                : ""
                        }                     
                        `}>
                    <span className={"status-text-title"}>
                        {getStatusText(props.accountStatus, props.config?.status)}
                    </span>
                    {props.config?.subtitle?.enabled && (
                        <span className="status-text-subtitle">
                            {props.config?.subtitle?.text ?? ""}
                        </span>
                    )}
                </div>

                <ButtonGroup>
                    {hasRightMenu && (
                        <MenuButton onClick={toggleDrawer} tabIndex={menuButtonTabIndex} />
                    )}

                    {props.canRefresh && (
                        <RefreshButton
                            onClick={props.refreshOnClick}
                            tabIndex={props.config?.actions?.refreshTabIndex}
                            showInLeft={false}
                            showInRight={false}
                        />
                    )}

                    {props.canMinimize && (
                        <MinimizeButton
                            onClick={props.minimizeOnClick}
                            tabIndex={props.config?.actions?.minimizeTabIndex}
                            showInRight={false}
                        />
                    )}

                    {props.canCancel && (
                        <CancelButton
                            onClick={props.cancelOnClick}
                            tabIndex={props.config?.actions?.cancelTabIndex}
                        />
                    )}
                </ButtonGroup>
            </div>
            {drawerOpen ? (
                <div className="xa-chat-menu-container">
                    <ChatMenu
                        openFrom={showMenuRight ? "right" : "left"}
                        opened={drawerOpen}
                        tabIndex={menuItemsTabIndex}
                        onItemClick={handleMenuItem}
                        items={menuItems}
                    />
                </div>
            ) : (
                <></>
            )}
        </>
    );
};

export default ChatHeader;
